import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useOrderState } from '@hooks/useOrderState';
import { DeliveryOptions } from './components/DeliveryOptions';
import { PickUpOptions } from './components/PickUpOptions';
import { DeliveryType } from '@interfaces';
import { getRoute, getCommuneZone } from '@utils';
import {
  setDeliveryTypeMutation,
  SetDeliveryTypeMutationResponse,
  SetDeliveryTypeMutationParams,
} from '@queries/checkout/mutations/setOrderSeliveryType';
import {
  getCheckoutAddress,
  GetCheckoutAddressQueryResponse,
  GetCheckoutAddressQueryParams,
} from '@queries/checkout/queries/address';
import { useAddressFormData } from '@screens/address/hooks/useAddressFormData';
import { useOpenReplayTracking } from '@services/openreplay';
import { useAuth } from '@hooks/useAuth';
import { BackgroundGlobalStyle } from '@components/PageWrapper';
import { PageWrapper } from '@components/PageWrapper';
import { HorizontalContainer, CheckoutCardsContainer } from '../styles';
import { SimpleHeader } from '@components/Header/SimpleHeader';
import { useRouter } from 'next/router';
import { OrderSummary } from '../components/OrderSummary';
import { useWindowSize } from '@hooks/useWindowSize';
import { SubmitButton } from '@components/FormComponents';
import { ErrorTypography } from '../styles';
import { CheckoutTitle } from '../components/Title';
import { usePermissions } from '@hooks/usePermissions';
import { useAssistantPaymentOptions } from '../paymentMethod/zustand/assistantPaymentOptionsStore';
import { FREE_SHIPPING_MIN_AMOUNT } from '@config/constants';
import { getDeliveryCosts } from '../utils/getDeliveryCosts';
import { DELIVERY_COST } from '@config/constants';
import { ScreenEventType } from '@validators/Events/Screen';
import { useEventTracking } from '@hooks/useEventTracking';
import { InteractionEventTypes } from '@validators/Events/Interactions';

export function CheckoutDeliveryPage() {
  const router = useRouter();
  const { addressUuid } = router.query;
  const { isTablet, isDesktop } = useWindowSize();
  const { track } = useEventTracking();
  const { trackEvent } = useOpenReplayTracking();
  const { appUser: user } = useAuth({ forceAuth: true });
  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');
  const isAssistantUser = userCan('MANAGE_USER');

  const { orderUuid } = useOrderState();

  const { setData, setQuery } = useAddressFormData();

  const [missingInfo, setMissingInfo] = useState(false);
  const addressSelectedRef = useRef(false);

  const {
    data: { addresses, currentOrder } = {},
    loading: loadingCurrentOrder,
    refetch: refetchAddresses,
  } = useQuery<GetCheckoutAddressQueryResponse, GetCheckoutAddressQueryParams>(
    getCheckoutAddress(),
    {
      variables: { params: { orderUuid } },
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (!loadingCurrentOrder) {
      void track(ScreenEventType.ScreenCheckoutDeliveryAddressView, {
        address_quantity: addresses.length,
      });
    }
  }, [addresses]);

  useEffect(() => {
    if (addressUuid) {
      void refetchAddresses();
    }
  }, [addressUuid, refetchAddresses]);

  const [setDeliveryTypeDatabase] = useMutation<
    SetDeliveryTypeMutationResponse,
    SetDeliveryTypeMutationParams
  >(setDeliveryTypeMutation(), {
    optimisticResponse({ params }) {
      return {
        setOrderDeliveryType: {
          __typename: 'Order',
          id: currentOrder?.id,
          deliveryType: params.deliveryType,
          orderGroupId: null,
          address:
            params.deliveryType === 'DELIVERY' ? currentOrder?.address : null,
        },
      };
    },
  });

  const { chargeDeliveryCost: AssistantChargeDeliveryCost } =
    useAssistantPaymentOptions();

  const chargeDeliveryCost = isAssistantUser
    ? AssistantChargeDeliveryCost ?? true
    : currentOrder?.metadata?.chargeDeliveryCost ?? true;

  const hasDeliveryCoupons = user?.deliveryCoupons?.count > 0;

  const { deliveryCost, oldDeliveryCost } =
    !currentOrder || !chargeDeliveryCost
      ? { deliveryCost: 0, oldDeliveryCost: 0 }
      : getDeliveryCosts({
          orders: [
            {
              deliveryCoupon: hasDeliveryCoupons,
              totalCost: currentOrder?.totalCost,
            },
          ],
          deliveryCost:
            hasDeliveryCoupons ||
            currentOrder?.totalCost - currentOrder?.deliveryCost >
              FREE_SHIPPING_MIN_AMOUNT
              ? 0
              : DELIVERY_COST,
        });
  const realDeliveryCost =
    currentOrder?.deliveryType === 'DELIVERY' ? deliveryCost : 0;

  const notifySelectDelivery = useCallback(
    function handleSelectAddressEvent(deliveryType: DeliveryType) {
      const commune =
        deliveryType === 'DELIVERY' ? currentOrder?.address?.commune : null;
      const zone =
        deliveryType === 'DELIVERY'
          ? getCommuneZone(currentOrder?.address?.commune)?.zoneType
          : null;
      void track(InteractionEventTypes.SelectAddress, {
        delivery_type: deliveryType,
        commune,
        zone,
      });
    },
    [currentOrder?.address?.commune, track],
  );

  const onSelectOption = useCallback(
    (deliveryType: DeliveryType) => {
      if (currentOrder?.deliveryType !== deliveryType) {
        void setDeliveryTypeDatabase({
          variables: { params: { orderUuid, deliveryType } },
        });
      }
      notifySelectDelivery(deliveryType);
      setMissingInfo(false);
    },
    [
      currentOrder?.deliveryType,
      orderUuid,
      setDeliveryTypeDatabase,
      notifySelectDelivery,
    ],
  );

  const handleNext = useCallback(() => {
    void track(InteractionEventTypes.ClickNext, {
      location: 'checkout_delivery_address',
    });
    const nextRoute = getRoute('checkoutDetail').toString();
    if (
      !currentOrder?.deliveryType ||
      (currentOrder.deliveryType === 'DELIVERY' && !currentOrder.address)
    ) {
      setMissingInfo(true);
      // if (!addressSelectedRef.current) {
      //   notifySelectDelivery(currentOrder.deliveryType);
      // }
      return;
    }

    if (currentOrder.deliveryType === 'PICK_UP') {
      return void router.push(nextRoute);
    }
    if (!currentOrder?.address?.isConfirmed) {
      void trackEvent('Address select missing confirmation coords', {
        address: currentOrder?.address,
      });
      const coords = currentOrder?.address?.coords;
      setData({
        ...currentOrder.address,
        metadata: coords
          ? {
              coords: {
                lat: +coords.split(',')[0],
                lng: +coords.split(',')[1],
              },
            }
          : {},
      });
      if (!coords) {
        void trackEvent('Address select missing coords', {
          address: currentOrder?.address,
        });
        setQuery(
          `${currentOrder?.address?.street} ${currentOrder?.address?.streetNumber}, ${currentOrder?.address?.commune}`,
        );
      }

      return void router.push(nextRoute);
    }
    return void router.push(nextRoute);
  }, [
    currentOrder?.address,
    currentOrder?.deliveryType,
    // notifySelectDelivery,
    router,
    setData,
    setQuery,
    track,
    trackEvent,
  ]);

  useEffect(
    function checkoutStart() {
      if (!currentOrder?.orderDetails?.length && !loadingCurrentOrder) {
        return void router.push(getRoute('home'));
      }
    },
    [
      track,
      currentOrder?.orderDetails?.length,
      user,
      currentOrder,
      loadingCurrentOrder,
      router,
    ],
  );

  const handleBack = useCallback(() => {
    void router.push(getRoute('lastWhim'));
  }, [router]);

  const loading = (loadingCurrentOrder && !addresses) || !user;
  const headerTitle = !isDesktop ? 'Tipo de entrega' : null;

  return (
    <>
      <BackgroundGlobalStyle mobileColor="gray" />
      <SimpleHeader
        backButton
        homeButton={isDesktop}
        title={headerTitle}
        color="white"
        handleBack={handleBack}
      />
      <PageWrapper addHeaderPadding={false} disableMobilePadding>
        <HorizontalContainer>
          <CheckoutCardsContainer>
            {(isDesktop || isTablet) && (
              <CheckoutTitle title={'Elige el tipo de entrega'} />
            )}
            <DeliveryOptions
              addresses={addresses}
              currentOrder={currentOrder}
              deliveryCost={deliveryCost}
              oldDeliveryCost={oldDeliveryCost}
              onSelectOption={() => onSelectOption('DELIVERY')}
              loading={loading}
              missingInfo={missingInfo}
            />
            <PickUpOptions
              currentOrder={currentOrder}
              onSelectOption={() => onSelectOption('PICK_UP')}
              loading={loading}
              missingInfo={missingInfo}
            />
            {missingInfo && (
              <ErrorTypography>
                Debes seleccionar una opción para continuar
              </ErrorTypography>
            )}
            {!isDesktop && (
              <SubmitButton disabled={loading} onClick={handleNext}>
                Siguiente
              </SubmitButton>
            )}
          </CheckoutCardsContainer>
          {isDesktop && (
            <OrderSummary
              isDelivery={
                !isLocalVendor && currentOrder?.deliveryType === 'DELIVERY'
              }
              deliveryCost={realDeliveryCost}
              oldDeliveryCost={oldDeliveryCost}
              handleNext={handleNext}
              loading={loading}
              showInsurance={false}
              showMekiCredits={false}
              showDelivery={Boolean(currentOrder?.deliveryType)}
            />
          )}
        </HorizontalContainer>
      </PageWrapper>
    </>
  );
}
